import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React from 'react';
import { createRoot } from 'react-dom/client';
import styled, { ThemeProvider } from 'styled-components';
import {
  Blocker,
  DataClientProviderBridge,
  DataProvider,
  DesignSysEnvUtil,
  ErrorBoundary,
  LightTheme,
  LoggerUtil,
  ServiceWorkerProvider,
  Suspense,
  Toasts,
  ConsumeWarningsUtil,
  PendoInitializer,
} from '@axiom/ui';
import { Provider } from 'react-redux';
import { BrowserRouter, useNavigate } from 'react-router';

import themes from './themes';
import { EnvUtil } from './utils/env-util';
import { Router } from './Router';
import { LoginContextProvider } from './components/LoginContext/LoginContextProvider';

import '@progress/kendo-theme-default/dist/all.css';
import '@axiom/ui/src/public/static/kendo-overrides.css';
import '@axiom/ui/src/public/static/bootstrap.min.css';
// eslint-disable-next-line import/no-unresolved
import 'semantic-ui-less/semantic.less';
import '@axiom/ui/src/semantic-ui-components.less';
import '@axiom/ui/src/public/static/main.css';
import '@axiom/ui/src/public/static/main-internal.css';
import '@axiom/ui/src/public/static/css-helpers.css';

const AppWrapper = styled.div``;

DesignSysEnvUtil.initialize({
  COOKIE_DOMAIN: EnvUtil.cookieDomain,
  JWT_COOKIE_KEY: EnvUtil.userCookieName,
  ENABLE_SUGGESTIONS: EnvUtil.enableSuggestions,
});

if (EnvUtil.enableLogging) {
  LoggerUtil.initialize(
    EnvUtil.logglyClientId,
    'login',
    EnvUtil.environmentName
  );
}

/**
 * Temp disable until we can get to this ticket
 * APCORE-2484
 */
if (process.env.NODE_ENV !== 'production') {
  ConsumeWarningsUtil.consumePropWarnings();
}

const Bootstrap = () => {
  const handleError = error => {
    const errorCode = error?.getStatusCode() ?? null;

    // We're outside the router so we need to do regular window redirects
    switch (errorCode) {
      case 404: {
        window.location.href = '/not-found';
        break;
      }
      default: {
        window.location.href = '/oops';
      }
    }
  };

  return (
    <AppWrapper>
      <ThemeProvider theme={themes.main}>
        <DataProvider>
          <DataClientProviderBridge />
          <ErrorBoundary errorLayout={handleError} ignoreUnauthorizedError>
            <LightTheme backgroundName="none">
              <Blocker>
                <Toasts />
                <Suspense>
                  <PendoInitializer pendoAppId={EnvUtil.pendoAppId} />
                  <BrowserRouter future={{ v7_startTransition: true }}>
                    <ServiceWorkerProvider />
                    <LoginContextProvider>
                      <Router />
                    </LoginContextProvider>
                  </BrowserRouter>
                </Suspense>
              </Blocker>
            </LightTheme>
          </ErrorBoundary>
        </DataProvider>
      </ThemeProvider>
    </AppWrapper>
  );
};

const root = createRoot(document.querySelector('#root'));
root.render(<Bootstrap />);
